import {
  arrayIfSingle
} from "./chunk-ZAADLBSB.js";
import {
  encodeApplogAndGetCid,
  getApplogTypeErrors,
  isEncryptedApplog,
  isValidApplog
} from "./chunk-KPMINROC.js";

// src/thread/basic.ts
import { Logger as Logger2 } from "besonders-logger";

// src/applog/applog-utils.ts
import { Logger } from "besonders-logger";
import { isBefore } from "date-fns";
import { partial, pick } from "lodash-es";
import { isEqual } from "lodash-es";
import stringify from "safe-stable-stringify";
var { WARN, LOG, DEBUG, VERBOSE, ERROR } = Logger.setup(Logger.INFO);
var applogSummary = (log) => {
  if (!log) return "\u2205";
  const a = log;
  const cid = a.cid ? `#${String(a.cid).slice(-6)}` : "";
  if (isEncryptedApplog(log)) return `enc[${log.enc.length}B]${cid}`;
  const vl = a.vl === void 0 ? "\u2205" : JSON.stringify(a.vl) ?? String(a.vl);
  const vlStr = vl.length > 32 ? `${vl.slice(0, 32)}\u2026` : vl;
  return `${a.en}/${a.at}=${vlStr}${a.ts ? `@${a.ts}` : ""}${cid}`;
};
var applogsSummary = (logs, sample = 3) => {
  if (!logs) return "\u2205";
  if (!logs.length) return "[0]";
  const shown = logs.slice(0, sample).map(applogSummary).join(", ");
  return `[${logs.length}] ${shown}${logs.length > sample ? ` \u2026+${logs.length - sample}` : ""}`;
};
var isoDateStrCompare = (strA, strB, dir = "asc") => dir === "asc" ? strA.localeCompare(strB, "en-US") : strB.localeCompare(strA, "en-US");
var objEqualByKeys = (keys, objA, objB) => {
  return isEqual(pick(objA, keys), pick(objB, keys));
};
var valueEq = (a, b) => isEqual(a, b);
var valueKey = (vl) => {
  switch (typeof vl) {
    case "string":
    case "number":
    case "boolean":
      return vl;
    default:
      return vl === null ? null : "\0obj:" + stringify(vl);
  }
};
var compareApplogsByTs = (logA, logB, dir = "asc") => {
  const tsCmp = isoDateStrCompare(logA.ts, logB.ts, dir);
  if (tsCmp !== 0) return tsCmp;
  return dir === "asc" ? logA.cid.localeCompare(logB.cid) : logB.cid.localeCompare(logA.cid);
};
var compareApplogsByEnAt = partial(objEqualByKeys, ["en", "at"]);
function isLaterByTsAndPv(a, b) {
  const tsCmp = isoDateStrCompare(a.ts, b.ts);
  if (tsCmp !== 0) return tsCmp > 0;
  if (a.en === b.en && a.at === b.at) {
    if (a.pv === b.cid) return true;
    if (b.pv === a.cid) return false;
  }
  return a.cid.localeCompare(b.cid) > 0;
}
function sortApplogsByTs(appLogArray, dir = "asc") {
  appLogArray.sort((a, b) => compareApplogsByTs(a, b, dir));
  let i = 0;
  while (i < appLogArray.length) {
    let j = i + 1;
    while (j < appLogArray.length && appLogArray[j].ts === appLogArray[i].ts) j++;
    if (j - i > 1) chainStabilizeCluster(appLogArray, i, j, dir);
    i = j;
  }
  return appLogArray;
}
function chainStabilizeCluster(applogs, from, to, dir) {
  const groups = /* @__PURE__ */ new Map();
  for (let k = from; k < to; k++) {
    const log = applogs[k];
    const key = log.en + "|" + log.at;
    const existing = groups.get(key);
    if (existing) existing.push(log);
    else groups.set(key, [log]);
  }
  if (groups.size === to - from) return;
  const orderedByGroup = /* @__PURE__ */ new Map();
  for (const [key, logs] of groups) {
    orderedByGroup.set(key, logs.length === 1 ? logs : topoSortByPv(logs, dir));
  }
  const cursors = /* @__PURE__ */ new Map();
  for (let k = from; k < to; k++) {
    const key = applogs[k].en + "|" + applogs[k].at;
    const cursor = cursors.get(key) ?? 0;
    applogs[k] = orderedByGroup.get(key)[cursor];
    cursors.set(key, cursor + 1);
  }
}
function topoSortByPv(logs, dir) {
  const cidSet = new Set(logs.map((l) => l.cid));
  const childrenByPv = /* @__PURE__ */ new Map();
  const roots = [];
  for (const log of logs) {
    if (log.pv && cidSet.has(log.pv)) {
      const children = childrenByPv.get(log.pv);
      if (children) children.push(log);
      else childrenByPv.set(log.pv, [log]);
    } else {
      roots.push(log);
    }
  }
  const lexCmp = (a, b) => a.cid.localeCompare(b.cid);
  roots.sort(lexCmp);
  for (const children of childrenByPv.values()) children.sort(lexCmp);
  const result = [];
  const visited = /* @__PURE__ */ new Set();
  const stack = [...roots].reverse();
  while (stack.length > 0) {
    const log = stack.pop();
    if (visited.has(log.cid)) continue;
    visited.add(log.cid);
    result.push(log);
    const children = childrenByPv.get(log.cid);
    if (children) {
      for (let i = children.length - 1; i >= 0; i--) stack.push(children[i]);
    }
  }
  if (result.length < logs.length) {
    for (const log of logs) if (!visited.has(log.cid)) result.push(log);
  }
  return dir === "desc" ? result.reverse() : result;
}
var isTsBefore = (log, logToCompare) => isBefore(new Date(log.ts), new Date(logToCompare.ts));
var uniqueEnFromAppLogs = (appLogArray) => [...new Set(appLogArray.map((eachLog) => eachLog.en))];
var areApplogsEqual = (logA, logB) => isEqual(logA, logB);
var warnMissingRemoveDuplicateMode = () => {
  WARN(`[removeDuplicateAppLogs] mode not set; pass 'safety' or 'cleanup' for optimal behavior`);
};
var removeDuplicateAppLogsCleanup = (appLogArray) => {
  const logMap = /* @__PURE__ */ new Map();
  const verboseEnabled = VERBOSE.isEnabled;
  for (const eachLog of appLogArray) {
    if (!eachLog) {
      ERROR(`falsy entry in applogs`, appLogArray);
      throw new Error(`falsy entry in applogs`);
    }
    if (!eachLog.cid) {
      ERROR(`applog with missing CID`, eachLog);
      throw new Error(`applog with missing CID`);
    }
    const key = eachLog.cid;
    const existing = logMap.get(key);
    if (existing) {
      if (verboseEnabled) VERBOSE(`Skipping duplicate applog:`, [existing, eachLog]);
    } else {
      logMap.set(key, eachLog);
    }
  }
  return Array.from(logMap.values());
};
var removeDuplicateAppLogsSafety = (appLogArray) => {
  const seen = /* @__PURE__ */ new Set();
  const verboseEnabled = VERBOSE.isEnabled;
  const existingByCid = verboseEnabled ? /* @__PURE__ */ new Map() : null;
  let result = null;
  let index = 0;
  for (const eachLog of appLogArray) {
    if (!eachLog) {
      ERROR(`falsy entry in applogs`, appLogArray);
      throw new Error(`falsy entry in applogs`);
    }
    if (!eachLog.cid) {
      ERROR(`applog with missing CID`, eachLog);
      throw new Error(`applog with missing CID`);
    }
    const key = eachLog.cid;
    if (seen.has(key)) {
      if (!result) {
        result = appLogArray.slice(0, index);
      }
      if (verboseEnabled) VERBOSE(`Skipping duplicate applog:`, [existingByCid?.get(key), eachLog]);
    } else {
      seen.add(key);
      if (existingByCid) existingByCid.set(key, eachLog);
      if (result) result.push(eachLog);
    }
    index++;
  }
  return result ?? appLogArray;
};
var removeDuplicateAppLogs = (appLogArray, mode) => {
  if (!mode) {
    warnMissingRemoveDuplicateMode();
    return removeDuplicateAppLogsCleanup(appLogArray);
  }
  return mode === "safety" ? removeDuplicateAppLogsSafety(appLogArray) : removeDuplicateAppLogsCleanup(appLogArray);
};
var hashToEntityId = (stringifiable, lngth = 8) => cyrb53hash(stringify(stringifiable), 31, lngth);
var mintNewEntity = (seed, lngth = 8) => hashToEntityId(seed, lngth);
var deriveDeterministicEntityID = (stableKey, lngth = 8) => hashToEntityId(stableKey, lngth);
function isVariable(x) {
  return typeof x === "string" && x.startsWith("?");
}
function variableNameWithoutQuestionmark(str) {
  return str.slice(1);
}
function isStaticPattern(x) {
  if (!["string", "boolean", "number", "function"].includes(typeof x)) WARN(`Unhandled pattern value type:`, typeof x, x);
  return !isVariable(x) && ["string", "boolean", "number"].includes(typeof x);
}
function resolveOrRemoveVariables(pattern, candidate) {
  let variablesToFill = {};
  const newPattern = {};
  for (const [patternKey, patternValue] of Object.entries(pattern)) {
    if (isVariable(patternValue)) {
      const varName = variableNameWithoutQuestionmark(patternValue);
      const candidateValue = candidate[varName];
      if (candidateValue) {
        newPattern[patternKey] = candidateValue;
      } else {
        variablesToFill[patternKey] = varName;
      }
    } else {
      newPattern[patternKey] = patternValue;
    }
  }
  return [newPattern, variablesToFill];
}
function matchVariable(variable, triplePart, context) {
  if (context.hasOwnProperty(variable)) {
    const bound = context[variable];
    const match = matchPart(bound, triplePart, context);
    return match;
  }
  return { ...context, [variable]: triplePart };
}
function matchPartStatic(field, patternPart, atomPart) {
  let result;
  if (patternPart) {
    const typ = typeof patternPart;
    if (typ === "string") {
      result = patternPart === atomPart;
    } else if (typ === "function") {
      result = patternPart(atomPart);
    } else if (patternPart instanceof Set) {
      result = patternPart.has(atomPart);
    } else {
      result = valueEq(patternPart, atomPart);
    }
  } else {
    result = patternPart === atomPart;
  }
  if (field.charAt(0) === "!") {
    return !result;
  } else {
    return result;
  }
}
function matchPart(patternPart, atomPart, context) {
  if (!context) {
    return null;
  }
  if (typeof patternPart === "string") {
    if (isVariable(patternPart)) {
      return matchVariable(patternPart, atomPart, context);
    }
  }
  if (typeof patternPart === "function") {
    return patternPart(atomPart) ? context : null;
  }
  if (patternPart instanceof Set) {
    return patternPart.has(atomPart) ? context : null;
  }
  return valueEq(patternPart, atomPart) ? context : null;
}
function matchPattern(pattern, applog, context) {
  return Object.entries(pattern).reduce((context2, [field, patternValue]) => {
    const applogValue = applog[field];
    const patternValT = patternValue;
    return matchPart(patternValT, applogValue, context2);
  }, context);
}
function actualize(context, find) {
  return Object.fromEntries(find.map((findField) => {
    if (context === null) {
      throw new Error(`actualize context is null ${find}`);
    }
    return [
      isVariable(findField) ? findField.replace(/^\?/, "") : findField,
      isVariable(findField) ? context[findField] : findField
    ];
  }));
}
var sum = function sum2(array) {
  var num = 0;
  for (var i = 0, l = array.length; i < l; i++) num += array[i];
  return num;
};
var mean = function mean2(array) {
  return sum(array) / array.length;
};
var arrStats = {
  max: function(array) {
    return Math.max.apply(null, array);
  },
  min: function(array) {
    return Math.min.apply(null, array);
  },
  range: function(array) {
    return arrStats.max(array) - arrStats.min(array);
  },
  midrange: function(array) {
    return arrStats.range(array) / 2;
  },
  sum,
  mean,
  average: mean,
  median: function(array) {
    array.sort(function(a, b) {
      return a - b;
    });
    var mid = array.length / 2;
    return mid % 1 ? array[mid - 0.5] : (array[mid - 1] + array[mid]) / 2;
  },
  modes: function(array) {
    if (!array.length) return [];
    var modeMap = {}, maxCount = 0, modes = [];
    array.forEach(function(val) {
      if (!modeMap[val]) modeMap[val] = 1;
      else modeMap[val]++;
      if (modeMap[val] > maxCount) {
        modes = [val];
        maxCount = modeMap[val];
      } else if (modeMap[val] === maxCount) {
        modes.push(val);
        maxCount = modeMap[val];
      }
    });
    return modes;
  },
  variance: function(array) {
    var mean3 = arrStats.mean(array);
    return arrStats.mean(array.map(function(num) {
      return Math.pow(num - mean3, 2);
    }));
  },
  standardDeviation: function(array) {
    return Math.sqrt(arrStats.variance(array));
  },
  meanAbsoluteDeviation: function(array) {
    var mean3 = arrStats.mean(array);
    return arrStats.mean(array.map(function(num) {
      return Math.abs(num - mean3);
    }));
  },
  zScores: function(array) {
    var mean3 = arrStats.mean(array);
    var standardDeviation = arrStats.standardDeviation(array);
    return array.map(function(num) {
      return (num - mean3) / standardDeviation;
    });
  }
};
arrStats.average = arrStats.mean;
var tsNearlySame = (timeA, timeB) => timeB.startsWith(timeA.slice(0, timeA.length - 4));
var cyrb53hash = function(str, seed = 13, strLength) {
  if (!str?.length) {
    throw new Error(`Empty string: ${str}`);
  }
  let h1 = 3735928559 ^ seed;
  let h2 = 1103547991 ^ seed;
  for (let i = 0, ch; i < str.length; i++) {
    ch = str.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ h1 >>> 16, 2246822507) ^ Math.imul(h2 ^ h2 >>> 13, 3266489909);
  h2 = Math.imul(h2 ^ h2 >>> 16, 2246822507) ^ Math.imul(h1 ^ h1 >>> 13, 3266489909);
  const asHex = (4294967296 * (2097151 & h2) + (h1 >>> 0)).toString(16);
  return asHex.slice(-strLength).padStart(strLength, "0");
};
function arraysContainSameElements(arr1, arr2) {
  if (arr1.length !== arr2.length) {
    return false;
  }
  const sortedArr1 = [...arr1].sort();
  const sortedArr2 = [...arr2].sort();
  for (let i = 0; i < sortedArr1.length; i++) {
    if (sortedArr1[i] !== sortedArr2[i]) {
      return false;
    }
  }
  return true;
}
function dateNowIso() {
  const now = /* @__PURE__ */ new Date();
  return now.toISOString();
}

// src/query/subscribable.ts
var SubscribableImpl = class {
  _value;
  _derivedSubscribers = [];
  _subscribers = [];
  _upstreamActive = false;
  _activateUpstream;
  _deactivateUpstream = null;
  _equals;
  constructor(initialValue, activateUpstream, opts) {
    this._value = initialValue;
    this._activateUpstream = activateUpstream ?? null;
    this._equals = opts?.equals === false ? () => false : opts?.equals ?? ((a, b) => a === b);
  }
  get value() {
    return this._value;
  }
  subscribe(cb, type) {
    if (!this._upstreamActive && this._activateUpstream) {
      this._deactivateUpstream = this._activateUpstream();
      this._upstreamActive = true;
    }
    const list = type === "derived" ? this._derivedSubscribers : this._subscribers;
    list.push(cb);
    return () => {
      const idx = list.indexOf(cb);
      if (idx >= 0) list.splice(idx, 1);
      if (this._derivedSubscribers.length === 0 && this._subscribers.length === 0 && this._upstreamActive) {
        this._deactivateUpstream?.();
        this._deactivateUpstream = null;
        this._upstreamActive = false;
      }
    };
  }
  /** Update value and notify subscribers (skips if equals check passes) */
  _set(value) {
    if (this._equals(value, this._value)) return;
    this._value = value;
    this._notify();
  }
  _notify() {
    const derived = [...this._derivedSubscribers];
    for (const sub of derived) sub();
    const subs = [...this._subscribers];
    for (const sub of subs) sub();
  }
  dispose() {
    this._deactivateUpstream?.();
    this._deactivateUpstream = null;
    this._derivedSubscribers.length = 0;
    this._subscribers.length = 0;
    this._upstreamActive = false;
  }
};
function isArrayInitEvent(event) {
  return event.init !== void 0;
}
var SubscribableArrayImpl = class {
  _items;
  _derivedSubscribers = [];
  _subscribers = [];
  _upstreamActive = false;
  _activateUpstream;
  _deactivateUpstream = null;
  constructor(initialItems, activateUpstream) {
    this._items = initialItems;
    this._activateUpstream = activateUpstream ?? null;
  }
  get items() {
    return this._items;
  }
  get length() {
    return this._items.length;
  }
  subscribe(cb, type) {
    if (!this._upstreamActive && this._activateUpstream) {
      this._deactivateUpstream = this._activateUpstream();
      this._upstreamActive = true;
    }
    const list = type === "derived" ? this._derivedSubscribers : this._subscribers;
    list.push(cb);
    return () => {
      const idx = list.indexOf(cb);
      if (idx >= 0) list.splice(idx, 1);
      if (this._derivedSubscribers.length === 0 && this._subscribers.length === 0 && this._upstreamActive) {
        this._deactivateUpstream?.();
        this._deactivateUpstream = null;
        this._upstreamActive = false;
      }
    };
  }
  /** Push items and notify subscribers */
  _push(...items) {
    this._items.push(...items);
    this._notify({ added: items, removed: null });
  }
  /** Remove items and notify subscribers */
  _remove(items) {
    for (const item of items) {
      const idx = this._items.indexOf(item);
      if (idx >= 0) this._items.splice(idx, 1);
    }
    this._notify({ added: [], removed: items });
  }
  /** Full reset — replace all items */
  _reset(items) {
    this._items = items;
    this._notify({ init: [...this._items] });
  }
  _notify(event) {
    const derived = [...this._derivedSubscribers];
    for (const sub of derived) sub(event);
    const subs = [...this._subscribers];
    for (const sub of subs) sub(event);
  }
  dispose() {
    this._deactivateUpstream?.();
    this._deactivateUpstream = null;
    this._derivedSubscribers.length = 0;
    this._subscribers.length = 0;
    this._upstreamActive = false;
  }
};

// src/utils/debug-name.ts
import stringify2 from "safe-stable-stringify";
var createDebugName = ({ caller, thread, pattern, args }) => {
  args = args || pattern;
  const str = `${!Array.isArray(thread) && thread?.name ? thread.name + " | " : ""}${caller ?? "caller?"}${args ? `{${typeof args === "string" ? args : stringify2(args)}}` : ""}`;
  return str;
};
var createDebugNameObj = (args) => {
  return { name: createDebugName(args) };
};
function prettifyThreadName(input) {
  let depth = 0;
  let result = "";
  let insideCurlyBraces = 0;
  for (let i = 0; i < input.length; i++) {
    const char = input[i];
    if (char === "(") {
      result += char + "\n" + "	".repeat(++depth);
    } else if (char === ")") {
      result += "\n" + "	".repeat(--depth) + char;
    } else if (char === "," && insideCurlyBraces === 0) {
      result += char + "\n" + "	".repeat(depth);
    } else if (char === "{" && insideCurlyBraces === 0) {
      insideCurlyBraces++;
      result += char + "\n" + "	".repeat(depth + 1);
    } else if (char === "}" && insideCurlyBraces === 1) {
      insideCurlyBraces--;
      result += "\n" + "	".repeat(depth) + char;
    } else if (char === "{" && insideCurlyBraces > 0) {
      insideCurlyBraces++;
      result += char;
    } else if (char === "}" && insideCurlyBraces > 1) {
      insideCurlyBraces--;
      result += char;
    } else {
      result += char;
    }
  }
  return result;
}

// src/thread/basic.ts
var { WARN: WARN2, LOG: LOG2, DEBUG: DEBUG2, VERBOSE: VERBOSE2, ERROR: ERROR2 } = Logger2.setup(Logger2.INFO, { prefix: "[thread]" });
var isInitEvent = isArrayInitEvent;
var Thread = class {
  constructor(name, parents, filters, _applogs = []) {
    this.name = name;
    this._applogs = _applogs;
    this.parents = parents === null ? null : arrayIfSingle(parents);
    this.filters = filters;
    if (this.parents?.length === 0) {
      WARN2(`[Thread] empty parents array`, name);
    }
  }
  name;
  _applogs;
  filters;
  parents;
  _derivedSubscribers = [];
  _subscribers = [];
  /** Monotonic counter incremented on every mutation. Used by memoizedFn to invalidate caches. */
  _version = 0;
  /**
   * Memoized cid→membership lookups, rebuilt lazily on first read after a mutation.
   * Previously these getters rebuilt a fresh Set/Map on EVERY access, which turned a
   * bulk `insertMissing(N)` (N × hasApplog over a thread of M) into O(N·M) — pegging a
   * core for tens of minutes on ~90k-applog threads. Now the collection is built once
   * per mutation batch and reused across the whole filter loop. Invalidated in
   * {@link invalidateApplogCaches}, called from every `_applogs` mutation path.
   */
  _cidSet = null;
  _byCid = null;
  get readOnly() {
    if (this.parents.length !== 1) return true;
    return this.parents[0].readOnly;
  }
  insert(appLogsToInsert) {
    if (this.readOnly) throw ERROR2(`[Thread] insert() called on read-only thread:`, this.nameAndSizeUntracked);
    if (!this.parents) throw ERROR2(`[Thread] insert() called on non-writable thread without parents:`, this.nameAndSizeUntracked);
    if (this.parents?.length !== 1) throw ERROR2(`[Thread] insert() called on thread with multiple parents:`, this.nameAndSizeUntracked);
    return this.parents[0].insert(appLogsToInsert);
  }
  insertRaw(appLogsToInsert) {
    if (this.readOnly) throw ERROR2(`[Thread] insertRaw() called on read-only thread:`, this.nameAndSizeUntracked);
    if (!this.parents) throw ERROR2(`[Thread] insertRaw() called on non-writable thread without parents:`, this.nameAndSizeUntracked);
    if (this.parents?.length !== 1) throw ERROR2(`[Thread] insertRaw() called on thread with multiple parents:`, this.nameAndSizeUntracked);
    return this.parents[0].insertRaw(appLogsToInsert);
  }
  subscribe(callback, type) {
    const list = type === "derived" ? this._derivedSubscribers : this._subscribers;
    list.push(callback);
    return () => {
      const idx = list.indexOf(callback);
      if (idx >= 0) list.splice(idx, 1);
    };
  }
  /**
   * Drop memoized cid lookups. MUST be called from every path that mutates `_applogs`
   * (or reassigns it). {@link notifySubscribers} covers the notify-emitting mutations
   * (insertRaw, MappedThread remap/parent-update); non-notifying mutations (e.g.
   * {@link WriteableThread.purge}) must call this explicitly.
   */
  invalidateApplogCaches() {
    this._cidSet = null;
    this._byCid = null;
  }
  notifySubscribers(event) {
    this._version++;
    this.invalidateApplogCaches();
    DEBUG2(`[thread: ${this.name}] notifying`, this._derivedSubscribers.length, "derived +", this._subscribers.length, "subscribers of", {
      ...event,
      subs: this._subscribers
    });
    const derived = [...this._derivedSubscribers];
    for (const subscriber of derived) {
      subscriber(event);
    }
    const subs = [...this._subscribers];
    for (const subscriber of subs) {
      subscriber(event);
    }
  }
  // ── SubscribableArray<Applog> ──
  get items() {
    return this._applogs;
  }
  dispose() {
    this._derivedSubscribers.length = 0;
    this._subscribers.length = 0;
  }
  get applogs() {
    return this._applogs;
  }
  get applogsCids() {
    return this._applogs.map((l) => l.cid);
  }
  get applogsCidSet() {
    if (this._cidSet === null) {
      this._cidSet = new Set(this._applogs.map((l) => l.cid));
    }
    return this._cidSet;
  }
  map(fn) {
    return this.applogs.map(fn);
  }
  get findLast() {
    return this.applogs.findLast.bind(this.applogs);
  }
  get findFirst() {
    return this.applogs.find.bind(this.applogs);
  }
  get firstLog() {
    return this.applogs[0];
  }
  get latestLog() {
    return this.applogs[this.applogs.length - 1];
  }
  hasApplog(applog, byRef) {
    if (byRef) {
      return this.applogs.includes(applog);
    } else {
      if (!applog.cid) throw ERROR2(`[hasApplogs] applog without CID:`, applog);
      return this.hasApplogCid(applog.cid);
    }
  }
  hasApplogCid(cid) {
    return this.applogsCidSet.has(cid.toString());
  }
  get applogsByCid() {
    if (this._byCid === null) {
      this._byCid = new Map(this._applogs.map((log) => [log.cid, log]));
    }
    return this._byCid;
  }
  getApplog(cid) {
    return this.applogsByCid.get(cid.toString());
  }
  hasApplogWithDiffTs(applog) {
    return this.applogs.find((existing) => existing.en === applog.en && existing.at === applog.at && valueEq(existing.vl, applog.vl) && existing.ag === applog.ag);
  }
  // get stateHash() {
  // 	blakeHasher.init()
  // 	for (const log of this.applogs) {
  // 		blakeHasher.update(log.cid)
  // 	}
  // 	return blakeHasher.digest()
  // }
  get isEmpty() {
    return this.size === 0;
  }
  get size() {
    return this.applogs.length;
  }
  get length() {
    return this.applogs.length;
  }
  get untrackedSize() {
    return this.size;
  }
  get nameAndSizeUntracked() {
    return `${this.name} (${this.size})`;
  }
  get prettyName() {
    return prettifyThreadName(this.name);
  }
  get hasParents() {
    return !!this.parents?.length;
  }
  // Compact representation for server-side / plain console logging (Node's util.inspect).
  // ⚠ Only consulted by Node — browser devtools ignore this symbol and keep rendering the
  // live, expandable object, so passing `thread` to a logger stays clickable in devtools
  // while no longer splatting every applog + the whole parent chain + subscribers to stdout.
  [/* @__PURE__ */ Symbol.for("nodejs.util.inspect.custom")]() {
    try {
      return `${this.constructor.name}<${this.nameAndSizeUntracked}${this.filters?.length ? ` |${this.filters.join(",")}` : ""}>`;
    } catch {
      return `${this.constructor.name}<${this.name} (size?)>`;
    }
  }
};
var getLogsFromThread = (logsOrThread) => logsOrThread instanceof Thread ? logsOrThread.applogs : logsOrThread;
var StaticThread = class _StaticThread extends Thread {
  static fromArray(applogs, name) {
    return new _StaticThread(name || "static", null, [], applogs);
  }
  constructor(name, parents, filters, _applogs) {
    super(name, parents, filters, _applogs);
  }
  get readOnly() {
    return true;
  }
};

// src/thread/filters.ts
import { Logger as Logger5 } from "besonders-logger";
import stringify3 from "safe-stable-stringify";

// src/applog/applog-helpers.ts
import { Logger as Logger3 } from "besonders-logger";
var { WARN: WARN3, LOG: LOG3, DEBUG: DEBUG3, VERBOSE: VERBOSE3, ERROR: ERROR3 } = Logger3.setup(Logger3.INFO);
function ensureTsPvAndFinalizeApplogs(appLogsToInsert, threadForPv) {
  DEBUG3(`[ensureTsPvAndFinalizeApplogs] ENTER - ${appLogsToInsert.length} applogs, thread size=${threadForPv.size}`);
  const ts = dateNowIso();
  const currentThread = threadForPv;
  const seenInBatch = /* @__PURE__ */ new Set();
  for (const log of appLogsToInsert) {
    const key = log.en + "|" + log.at;
    if (seenInBatch.has(key)) {
      WARN3(
        `[ensureTsPvAndFinalizeApplogs] within-batch duplicate (en, at)=(${log.en}, ${log.at}) \u2014 chain-pv won't link these; consider separate insert calls`,
        { log }
      );
      break;
    }
    seenInBatch.add(key);
  }
  DEBUG3(`[ensureTsPvAndFinalizeApplogs] About to map over applogs`);
  const mapped = appLogsToInsert.map((log, idx) => {
    DEBUG3(`[ensureTsPvAndFinalizeApplogs] Processing applog ${idx + 1}/${appLogsToInsert.length}`);
    const result = finalizeApplogForInsert(log, { ts, threadForPv: currentThread });
    DEBUG3(`[ensureTsPvAndFinalizeApplogs] Finalized applog ${idx + 1}/${appLogsToInsert.length}`);
    return result;
  });
  DEBUG3(`[ensureTsPvAndFinalizeApplogs] EXIT - mapped ${mapped.length} applogs`);
  return mapped;
}
function ensureTsPvAndFinalizeApplog(applogToInsert, threadForPv) {
  return ensureTsPvAndFinalizeApplogs([applogToInsert], threadForPv)[0];
}
function finalizeApplogForInsert(log, { ts, threadForPv } = {}) {
  DEBUG3(`[finalizeApplogForInsert] ENTER - en=${log.en}, at=${log.at}`);
  DEBUG3(`[finalizeApplogForInsert] About to call withTs`);
  const logWithTs = withTs(log, ts ?? dateNowIso());
  DEBUG3(`[finalizeApplogForInsert] About to call withPvFrom (thread size=${threadForPv?.size ?? "null"})`);
  const logWithPv = withPvFrom(logWithTs, threadForPv);
  DEBUG3(`[finalizeApplogForInsert] About to call encodeApplogAndGetCid`);
  const cid = encodeApplogAndGetCid(logWithPv).toString();
  DEBUG3(`[finalizeApplogForInsert] CID created: ${cid}`);
  if (log.cid && log.cid !== cid) WARN3(`[finalizeApplogForInsert] overwriting wrong CID`, { log, cid, logWithPv });
  const logWithCid = { ...logWithPv, cid };
  DEBUG3(`[finalizeApplogForInsert] About to validate applog`);
  if (!isValidApplog(logWithCid)) {
    throw ERROR3(`Bogus Applog ${JSON.stringify(logWithCid)}`, getApplogTypeErrors(logWithCid));
  }
  DEBUG3(`[finalizeApplogForInsert] EXIT - CID=${cid}`);
  return Object.freeze(logWithCid);
}
function hasAg(log) {
  return !!log.ag;
}
function hasTs(log) {
  return !!log.ts;
}
function hasPv(log) {
  return !!log.pv;
}
function withTs(log, ts) {
  return hasTs(log) ? log : { ...log, ts };
}
function withAg(log, ag) {
  return hasAg(log) ? log : { ...log, ag };
}
function withPvFrom(log, thread) {
  DEBUG3(`[withPvFrom] ENTER - en=${log.en}, at=${log.at}, hasPv=${log.pv !== void 0}`);
  if (log.pv !== void 0) {
    DEBUG3(`[withPvFrom] EXIT early - pv already set`);
    return log;
  }
  if (!thread) {
    if (!hasPv(log)) throw ERROR3(`[withPvFrom] no thread and no pv:`, log);
    DEBUG3(`[withPvFrom] EXIT - no thread, returning log with existing pv`);
    return log;
  } else {
    const { en, at } = log;
    DEBUG3(`[withPvFrom] About to call thread.findLast for en=${en}, at=${at}, thread.size=${thread.size}`);
    const prevLog = thread.findLast((l) => l.en == en && l.at == at);
    DEBUG3(`[withPvFrom] findLast completed, found=${!!prevLog}`);
    DEBUG3(`[withPvFrom] About to check equality`);
    if (objEqualByKeys(["en", "at", "vl", "ts", "ag"], log, prevLog)) {
      throw ERROR3(`[withPvFrom] Same as previous:`, { log, pv: prevLog, thread });
    }
    const prevLogCid = (log.pv !== void 0 ? log.pv : prevLog?.cid) ?? null;
    DEBUG3(`[withPvFrom] EXIT - prevLogCid=${prevLogCid}`);
    return { ...log, pv: prevLogCid };
  }
}
function joinThreads(threads) {
  const derivation = {
    compute(parents) {
      if (parents.length < 2) DEBUG3(`joinThreads with count=${parents.length}`);
      return sortApplogsByTs(
        removeDuplicateAppLogs(parents.flatMap((s) => {
          const logs = s.applogs;
          if (!logs) {
            ERROR3(`falsy applogs of thread`, s);
            throw new Error(`falsy applogs of thread`);
          }
          return logs;
        }), "cleanup")
      );
    },
    mapDelta: (delta, { source, parents, state }) => ({
      added: delta.added.filter((log) => !state.includes(log)),
      removed: delta.removed?.filter(
        (log) => state.includes(log) && !parents.some((p) => p !== source && p.hasApplog(log, true))
      )
    })
  };
  return new MappedThread(
    `join(~ ${threads.map((s) => s.name).join(", ")})`,
    threads,
    ["?"],
    // HACK this basically says "we're not sure what filters are applied"
    derivation
  );
}

// src/query/memoized.ts
import { isEqual as isEqual2 } from "lodash-es";
function compareStructural(argsA, argsB, versionsA) {
  if (argsA.length !== argsB.length) return false;
  for (let i = 0; i < argsA.length; i++) {
    if (argsA[i] instanceof Thread) {
      if (argsB[i] !== argsA[i]) return false;
      if (versionsA && versionsA[i] !== void 0 && argsB[i]._version !== versionsA[i]) return false;
    } else {
      if (!isEqual2(argsA[i], argsB[i])) return false;
    }
  }
  return true;
}
function snapshotVersions(args) {
  return args.map((a) => a instanceof Thread ? a._version : void 0);
}
function memoizedFn(name, fn, opts) {
  const cache = [];
  const argsEqual = opts?.argsEqual ?? compareStructural;
  const maxSize = opts?.maxSize ?? Infinity;
  const resultTracksSource = opts?.resultTracksSource ?? false;
  return function(...args) {
    const existing = cache.find((entry) => argsEqual(entry.args, args, entry.versions));
    if (existing) return existing.result;
    const result = fn.apply(this, args);
    if (cache.length >= maxSize) {
      cache.shift();
    }
    cache.push({ args, versions: resultTracksSource ? void 0 : snapshotVersions(args), result });
    return result;
  };
}
function refCountedMemoizedFn(name, fn, opts) {
  const cache = [];
  const argsEqual = opts?.argsEqual ?? compareStructural;
  const gracePeriodMs = opts?.gracePeriodMs ?? 0;
  function evict(entry, args) {
    const idx = cache.indexOf(entry);
    if (idx >= 0) cache.splice(idx, 1);
    opts?.onCleanup?.(entry.result, ...args);
  }
  return function(...args) {
    let entry = cache.find((e) => argsEqual(e.args, args));
    if (entry) {
      if (entry.graceTimer !== null) {
        clearTimeout(entry.graceTimer);
        entry.graceTimer = null;
      }
      entry.refCount++;
    } else {
      const result = fn.apply(this, args);
      entry = { args, result, refCount: 1, graceTimer: null };
      cache.push(entry);
    }
    const capturedEntry = entry;
    let released = false;
    return {
      get value() {
        return capturedEntry.result;
      },
      release() {
        if (released) return;
        released = true;
        capturedEntry.refCount--;
        if (capturedEntry.refCount <= 0) {
          if (gracePeriodMs > 0) {
            capturedEntry.graceTimer = setTimeout(() => {
              if (capturedEntry.refCount <= 0) {
                evict(capturedEntry, args);
              }
            }, gracePeriodMs);
          } else {
            evict(capturedEntry, args);
          }
        }
      }
    };
  };
}

// src/thread/mapped.ts
import { Logger as Logger4 } from "besonders-logger";
import { sortedIndexBy } from "lodash-es";
var { WARN: WARN4, LOG: LOG4, DEBUG: DEBUG4, VERBOSE: VERBOSE4, ERROR: ERROR4 } = Logger4.setup(Logger4.INFO);
var threadEventSummary = (e) => isInitEvent(e) ? `init ${applogsSummary(e.init)}` : `added ${applogsSummary(e.added)}${e.removed ? ` removed ${applogsSummary(e.removed)}` : ""}`;
var MappedThread = class _MappedThread extends Thread {
  // mapped to unsubscribe function
  constructor(name, parents, filters, _derivation, _writeMapper = null, _readOnly) {
    const parentArr = arrayIfSingle(parents);
    if (!_derivation && parentArr.length > 1) {
      throw new Error(`MappedThread without derivation must have exactly one parent`);
    }
    const initialLogs = _derivation ? _derivation.compute(parentArr) : parentArr[0]._applogs;
    super(
      name,
      parents,
      filters,
      _derivation ? [...initialLogs] : initialLogs
      // clone if derived, share if not
    );
    this.name = name;
    this._derivation = _derivation;
    this._writeMapper = _writeMapper;
    this._readOnly = _readOnly;
    if (_derivation) {
      this.subscribeToParents();
    }
  }
  name;
  _derivation;
  _writeMapper;
  _readOnly;
  static mapWrites(parent, name, mapper) {
    return new _MappedThread(
      `${name}<${parent.nameAndSizeUntracked}>`,
      parent,
      parent.filters,
      null,
      // no derivation — share parent array
      mapper
    );
  }
  static asReadOnly(parent) {
    if (parent.readOnly) return parent;
    return new _MappedThread(
      `readOnly(${parent.name})`,
      parent,
      parent.filters,
      null,
      // no derivation — share parent array
      null,
      true
      // readOnly
    );
  }
  _parentSubscriptions = null;
  insert(appLogsToInsert) {
    if (this.readOnly) throw ERROR4(`[MappedThread] insert() called on read-only thread:`, this.nameAndSizeUntracked);
    const mapped = this._writeMapper ? this._writeMapper(appLogsToInsert) : appLogsToInsert;
    if (this._writeMapper && !mapped) return;
    return this.parents.forEach((parent) => parent.insert(mapped));
  }
  insertRaw(appLogsToInsert) {
    if (this.readOnly) throw ERROR4(`[MappedThread] insertRaw() called on read-only thread:`, this.nameAndSizeUntracked);
    const mapped = this._writeMapper ? this._writeMapper(appLogsToInsert) : appLogsToInsert;
    if (this._writeMapper && !mapped) return;
    return this.parents.forEach((parent) => parent.insertRaw(mapped));
  }
  subscribeToParents() {
    this._parentSubscriptions = /* @__PURE__ */ new Map();
    if (!this.parents.length) {
      WARN4(`MappedThread has no parents`, this);
    }
    VERBOSE4(`[MappedThread: ${this.name}] subscribing to parents:`, this.parents.map((p) => p.name));
    for (const p of this.parents) {
      VERBOSE4(`[MappedThread: ${this.name}] sub to parent`, p.nameAndSizeUntracked);
      const sub = this.onParentUpdate.bind(this, p);
      const unsubscribe = p.subscribe(sub, "derived");
      this._parentSubscriptions.set(p, unsubscribe);
    }
  }
  /** Tear down parent subscriptions and clear internal state */
  dispose() {
    if (this._parentSubscriptions) {
      for (const [, unsubscribe] of this._parentSubscriptions) {
        unsubscribe();
      }
      this._parentSubscriptions = null;
    }
  }
  /** Swap parents at runtime — re-subscribes and recomputes applogs, notifying downstream */
  setParents(newParents) {
    this.dispose();
    this.parents = newParents;
    this.subscribeToParents();
    this.triggerRemap();
  }
  subscribe(callback, type) {
    if (this._derivation && !this._parentSubscriptions) {
      this.subscribeToParents();
    }
    return super.subscribe(callback, type);
  }
  /** Recompute full state from parents via compute() */
  triggerRemap() {
    if (!this._derivation) throw ERROR4(`triggerRemap on a thread without derivation`, this.nameAndSizeUntracked);
    DEBUG4(`MappedThread{${this.nameAndSizeUntracked}} triggerRemap`);
    const newLogs = this._derivation.compute(this.parents);
    this._applogs.length = 0;
    this._applogs.push(...newLogs);
    this.notifySubscribers({ init: newLogs });
  }
  onParentUpdate(sourceThread, event) {
    if (!this._derivation) return;
    VERBOSE4(`MappedThread{${this.nameAndSizeUntracked}} parentUpdate`, event);
    let result;
    if (isInitEvent(event)) {
      const newLogs = this._derivation.compute(this.parents);
      result = { init: newLogs };
    } else if (this._derivation.mapDelta) {
      const ctx = {
        source: sourceThread,
        parents: this.parents,
        state: this._applogs
      };
      const mapped = this._derivation.mapDelta(event, ctx);
      if (mapped === null) {
        const newLogs = this._derivation.compute(this.parents);
        result = { init: newLogs };
      } else {
        result = mapped;
      }
    } else {
      const newLogs = this._derivation.compute(this.parents);
      result = { init: newLogs };
    }
    if (isInitEvent(result)) {
      this._applogs.length = 0;
      this._applogs.push(...result.init);
    } else {
      VERBOSE4(`MappedThread{${this.nameAndSizeUntracked}} parentUpdate => mapped`, result);
      for (const log of result.added) {
        this._applogs.splice(sortedIndexBy(this._applogs, log, "ts"), 0, log);
      }
      if (result.removed) {
        for (const toRemove of result.removed) {
          const idx = this._applogs.indexOf(toRemove);
          if (idx >= 0) {
            this._applogs.splice(idx, 1);
          } else {
            throw ERROR4(`MappedThread{${this.name}} toRemove: log not found`, toRemove, {
              thread: this.nameAndSizeUntracked,
              currentLogs: applogsSummary(this._applogs),
              event: threadEventSummary(event),
              result: threadEventSummary(result)
            });
          }
        }
      }
    }
    this.notifySubscribers(result);
  }
  get readOnly() {
    return this._readOnly ?? false;
  }
  // A no-derivation MappedThread SHARES its parent's `_applogs` array (see constructor) and never
  // mutates it itself — the parent does, and the parent invalidates only its own cid caches. So a
  // local cache here would go stale after a parent insert. Delegate membership lookups to the parent
  // (chaining up to the array's real owner), whose cache is always fresh. Derived threads own their
  // array and use the inherited, self-invalidating cache.
  get applogsCidSet() {
    return this._derivation ? super.applogsCidSet : this.parents[0].applogsCidSet;
  }
  get applogsByCid() {
    return this._derivation ? super.applogsByCid : this.parents[0].applogsByCid;
  }
};

// src/thread/filters.ts
var { WARN: WARN5, LOG: LOG5, DEBUG: DEBUG5, VERBOSE: VERBOSE5, ERROR: ERROR5 } = Logger5.setup(Logger5.INFO);
var rollingFilter2 = memoizedFn("rollingFilter", function rollingFilter3(thread, pattern, opts = {}) {
  const filter = makeFilter(pattern);
  const derivation = {
    compute: (parents) => {
      if (parents.length !== 1) {
        throw ERROR5(`rollingFilter requires exactly one parent`, { parents: parents.length });
      }
      return filter(parents[0].applogs);
    },
    mapDelta: (delta, { state }) => {
      const mappedDelta = {
        added: filter(delta.added),
        removed: delta.removed?.filter((log) => state.includes(log))
      };
      VERBOSE5(
        `rollingFilter{${thread.nameAndSizeUntracked} | ${opts.name ? ` '${opts.name}'}` : ""} parentUpdate`,
        pattern,
        delta,
        "=>",
        mappedDelta
      );
      return mappedDelta;
    }
  };
  return new MappedThread(
    `${thread.name} | ${opts.name || `rollingFilter{${stringify3(pattern)}}`}`,
    thread,
    [...thread.filters, ...opts.extraFilterName ? [opts.extraFilterName] : []],
    derivation
  );
}, {
  argsDebugName: (thread, pattern, opts) => createDebugName({ caller: "rollingFilter", thread, pattern, args: opts }),
  resultTracksSource: true
  // MappedThread subscribes to its parent and stays current
});
var rollingMapper = memoizedFn("rollingMapper", function rollingMapper2(thread, derivation, opts = {}) {
  return new MappedThread(
    `${thread.name} | ${opts.name || `rollingMapper`}`,
    thread,
    [...thread.filters, ...opts.extraFilterName ? [opts.extraFilterName] : []],
    derivation
  );
}, {
  argsDebugName: (thread, _derivation, opts) => createDebugName({ caller: "rollingMapper", thread, args: opts }),
  resultTracksSource: true
  // MappedThread subscribes to its parent and stays current
});
var rollingAcc = memoizedFn(
  "rollingAcc",
  function rollingAcc2(thread, acc, eventMapper, opts = {}) {
    eventMapper({ init: thread.applogs }, acc);
    const result = new SubscribableImpl(
      acc,
      () => thread.subscribe((event) => {
        eventMapper(event, acc);
        result._set(acc);
      }, "derived"),
      { equals: false }
      // accumulator is mutated in-place
    );
    return result;
  },
  { argsDebugName: (thread, _acc, _mapper, opts) => `rollingAcc{${thread.nameAndSizeUntracked}${opts?.name ? ` | ${opts?.name}` : ""}}` }
);
var getUntrackedPattern = function getUntrackedPattern2(pattern) {
  if (!Object.entries(pattern).length) {
    throw new Error(`Pattern is empty`);
  }
  return pattern;
};
function makeFilter(pattern) {
  return function madeFilter(logs) {
    return logs.filter(function madeFilterSingleLog(applog) {
      for (const field of Object.keys(pattern)) {
        let patternValue = pattern[field];
        if (patternValue === void 0) continue;
        const applogValue = applog[field.startsWith("!") ? field.slice(1) : field];
        const patternValT = patternValue;
        if (!matchPartStatic(field, patternValT, applogValue)) {
          return false;
        }
      }
      return true;
    });
  };
}
var getUntrackedFilterResults = function getUntrackedFilterResults2(thread, pattern, opts = {}) {
  const untrackedPattern = getUntrackedPattern(pattern);
  const filter = makeFilter(untrackedPattern);
  return filter(thread.applogs);
};
function hasFilter(thread, filter) {
  return thread.filters.includes(filter);
}
function assertRaw(thread) {
  if (thread.filters.length) {
    WARN5(`[assertRaw] but '${thread.nameAndSizeUntracked}' has filters:`, thread.filters);
  }
  return thread;
}
function assertOnlyCurrent(thread) {
  if (!hasFilter(thread, "lastWriteWins") || !hasFilter(thread, "withoutDeleted")) throw ERROR5(`should be filtered thread, but is:`, thread.filters);
  return thread;
}
var asReadOnly = MappedThread.asReadOnly;
var simpleApplogMapper = function simpleApplogMapper2(thread, logMapper, opts = {}) {
  const mappedTo = /* @__PURE__ */ new Map();
  const mapLogs = (applogs, thread2) => {
    const ts = dateNowIso();
    return applogs.map((log) => {
      const mapped = logMapper(log, thread2);
      let mapTo;
      if (mapped === log) {
        mapTo = log;
      } else {
        if (mapped.cid === log.cid) {
          delete mapped.cid;
        }
        mapTo = finalizeApplogForInsert(mapped, {
          ts,
          threadForPv: null
          // ? should not be inferred, right?
        });
      }
      mappedTo.set(log.cid, mapTo);
      return mapTo;
    });
  };
  const derivation = {
    compute(parents) {
      if (parents.length !== 1) {
        throw ERROR5(`simpleApplogMapper requires exactly one parent`, { parents: parents.length });
      }
      const [parent] = parents;
      mappedTo.clear();
      return mapLogs(parent.applogs, parent);
    },
    mapDelta: (delta, { state }) => {
      const mappedDelta = {
        added: mapLogs(delta.added, thread),
        removed: delta.removed?.map((removedSourceLog) => {
          const mappedLog = mappedTo.get(removedSourceLog.cid);
          if (!mappedLog) {
            throw ERROR5(`[simpleApplogMapper] Parent remove event for Applog that we don't know about`, { removedSourceLog });
          }
          mappedTo.delete(removedSourceLog.cid);
          return mappedLog;
        }).filter((log) => state.includes(log))
      };
      VERBOSE5(
        `simpleApplogMapper{${thread.nameAndSizeUntracked} | ${opts?.name ? ` '${opts?.name}'}` : ""} parentUpdate`,
        delta,
        "=>",
        mappedDelta
      );
      return mappedDelta;
    }
  };
  const mappedThread = rollingMapper(thread, derivation, opts);
  VERBOSE5.isDisabled || VERBOSE5(`simpleApplogMapper<${thread.nameAndSizeUntracked}> initial mapped to`, mappedThread.applogs);
  return mappedThread;
};

// src/thread/indexes.ts
import { Logger as Logger6 } from "besonders-logger";

// src/query/matchers.ts
function includes(str) {
  return (vl) => vl?.includes?.(str);
}
function includedIn(arr) {
  return (vl) => arr?.includes?.(vl);
}
function anyOf(...vals) {
  for (const v of vals) {
    if (v !== null && typeof v === "object") {
      throw new Error(
        `[anyOf] object/array members are not supported (referential Set membership would silently never match). Use a predicate matcher instead, e.g. (v) => members.some(m => isEqual(v, m)).`
      );
    }
  }
  return new Set(vals);
}

// src/thread/indexes.ts
var { WARN: WARN6, LOG: LOG6, DEBUG: DEBUG6, VERBOSE: VERBOSE6, ERROR: ERROR6 } = Logger6.setup(Logger6.INFO);
var applogsByEntity = memoizedFn(
  "applogsByEntity",
  function applogsByEntity2(thread) {
    const map = /* @__PURE__ */ new Map();
    const add = (log) => {
      let arr = map.get(log.en);
      if (!arr) {
        arr = [];
        map.set(log.en, arr);
      }
      arr.push(log);
    };
    const remove = (log) => {
      const arr = map.get(log.en);
      if (!arr) return;
      const idx = arr.indexOf(log);
      if (idx >= 0) arr.splice(idx, 1);
      if (arr.length === 0) map.delete(log.en);
    };
    for (const log of thread.applogs) add(log);
    thread.subscribe((event) => {
      if (isInitEvent(event)) {
        map.clear();
        for (const log of event.init) add(log);
      } else {
        for (const log of event.added) add(log);
        if (event.removed) {
          for (const log of event.removed) remove(log);
        }
      }
    }, "derived");
    return map;
  },
  { resultTracksSource: true }
  // live map, maintained by its own thread subscription
);
var applogsByAttrValue = memoizedFn(
  "applogsByAttrValue",
  function applogsByAttrValue2(thread, attr) {
    const map = /* @__PURE__ */ new Map();
    const add = (log) => {
      const key = valueKey(log.vl);
      let arr = map.get(key);
      if (!arr) {
        arr = [];
        map.set(key, arr);
      }
      arr.push(log);
    };
    const remove = (log) => {
      const key = valueKey(log.vl);
      const arr = map.get(key);
      if (!arr) return;
      const idx = arr.indexOf(log);
      if (idx >= 0) arr.splice(idx, 1);
      if (arr.length === 0) map.delete(key);
    };
    const filtered = rollingFilter2(thread, { at: attr });
    for (const log of filtered.applogs) add(log);
    const result = new SubscribableImpl(
      map,
      () => filtered.subscribe((event) => {
        if (isInitEvent(event)) {
          map.clear();
          for (const log of event.init) add(log);
        } else {
          if (event.removed) {
            for (const log of event.removed) remove(log);
          }
          for (const log of event.added) add(log);
        }
        result._set(map);
      }, "derived"),
      { equals: false }
    );
    return result;
  },
  { resultTracksSource: true }
  // live Subscribable, maintained by its own subscription
);
var entityLinkIndex = memoizedFn(
  "entityLinkIndex",
  function entityLinkIndex2(thread, attrA, attrB) {
    const byA = /* @__PURE__ */ new Map();
    const byB = /* @__PURE__ */ new Map();
    const aByLink = /* @__PURE__ */ new Map();
    const bByLink = /* @__PURE__ */ new Map();
    function addLink(linkId, aValue, bValue) {
      const entry = { linkId, aValue, bValue };
      let arrA = byA.get(aValue);
      if (!arrA) {
        arrA = [];
        byA.set(aValue, arrA);
      }
      arrA.push(entry);
      let arrB = byB.get(bValue);
      if (!arrB) {
        arrB = [];
        byB.set(bValue, arrB);
      }
      arrB.push(entry);
    }
    function removeLink(linkId, aValue, bValue) {
      const arrA = byA.get(aValue);
      if (arrA) {
        const idx = arrA.findIndex((e) => e.linkId === linkId);
        if (idx >= 0) arrA.splice(idx, 1);
        if (arrA.length === 0) byA.delete(aValue);
      }
      const arrB = byB.get(bValue);
      if (arrB) {
        const idx = arrB.findIndex((e) => e.linkId === linkId);
        if (idx >= 0) arrB.splice(idx, 1);
        if (arrB.length === 0) byB.delete(bValue);
      }
    }
    function processLog(log) {
      if (log.at === attrA) {
        aByLink.set(log.en, log.vl);
        const bVal = bByLink.get(log.en);
        if (bVal !== void 0) {
          addLink(log.en, log.vl, bVal);
        }
      } else if (log.at === attrB) {
        bByLink.set(log.en, log.vl);
        const aVal = aByLink.get(log.en);
        if (aVal !== void 0) {
          addLink(log.en, aVal, log.vl);
        }
      }
    }
    function removeLog(log) {
      if (log.at === attrA) {
        const aVal = aByLink.get(log.en);
        const bVal = bByLink.get(log.en);
        if (aVal !== void 0 && bVal !== void 0) {
          removeLink(log.en, aVal, bVal);
        }
        aByLink.delete(log.en);
      } else if (log.at === attrB) {
        const aVal = aByLink.get(log.en);
        const bVal = bByLink.get(log.en);
        if (aVal !== void 0 && bVal !== void 0) {
          removeLink(log.en, aVal, bVal);
        }
        bByLink.delete(log.en);
      }
    }
    function buildFull(applogs) {
      byA.clear();
      byB.clear();
      aByLink.clear();
      bByLink.clear();
      for (const log of applogs) processLog(log);
    }
    const filtered = rollingFilter2(thread, { at: anyOf(attrA, attrB) });
    buildFull(filtered.applogs);
    const value = { byA, byB };
    const result = new SubscribableImpl(
      value,
      () => filtered.subscribe((event) => {
        if (isInitEvent(event)) {
          buildFull(event.init);
        } else {
          if (event.removed) {
            for (const log of event.removed) removeLog(log);
          }
          for (const log of event.added) processLog(log);
        }
        result._set(value);
      }, "derived"),
      { equals: false }
    );
    return result;
  },
  { resultTracksSource: true }
  // live Subscribable, maintained by its own subscription
);

// src/thread/writeable.ts
import { Logger as Logger7 } from "besonders-logger";
var { WARN: WARN7, LOG: LOG7, DEBUG: DEBUG7, VERBOSE: VERBOSE7, ERROR: ERROR7 } = Logger7.setup(Logger7.INFO);
var WriteableThread = class extends Thread {
  constructor(name, applogs = [], filters) {
    super(name, null, filters, applogs);
  }
  purge(cidsToPurge) {
    const beforeCount = this.applogs.length;
    this._applogs = this.applogs.filter((log) => !cidsToPurge.includes(log.cid));
    this.invalidateApplogCaches();
    return beforeCount - this.applogs.length;
  }
  insert(appLogsToInsert) {
    DEBUG7(`[WriteableThread.insert] ENTER - ${appLogsToInsert.length} applogs for thread "${this.name}"`);
    DEBUG7(`[WriteableThread.insert] About to call ensureTsPvAndFinalizeApplogs`);
    const mapped = ensureTsPvAndFinalizeApplogs(appLogsToInsert, this);
    DEBUG7(`[WriteableThread.insert] ensureTsPvAndFinalizeApplogs completed, mapped=${mapped.length} applogs`);
    DEBUG7(`[WriteableThread.insert] About to call insertRaw`);
    const result = this.insertRaw(mapped);
    DEBUG7(`[WriteableThread.insert] insertRaw completed`);
    return result;
  }
  /**
   * Insert only applogs not already in this thread.
   * @param byRef If true, compares by reference; if false, compares by CID (default)
   * @returns The applogs that were actually inserted
   */
  insertMissing(appLogsToInsert, byRef = false) {
    const missing = appLogsToInsert.filter((log) => !this.hasApplog(log, byRef));
    if (missing.length === 0) {
      VERBOSE7(`[insertMissing] no missing applogs`);
      return [];
    }
    return this.insertRaw(missing) ?? [];
  }
  /**
   * Insert raw applogs directly into the thread.
   *
   * STRICT VALIDATION: This method throws errors for:
   * - Duplicate applogs in input array (programming error)
   * - Invalid applogs (missing required fields)
   * - Applogs already in thread (programming error)
   *
   * For external imports where duplicates are expected, use removeDuplicateAppLogs(..., 'cleanup')
   * before calling this method.
   *
   * @param appLogsToInsert Must be deduplicated and validated. Needs to be mutable because it will be sorted
   * 	(and if you need to clone it, so do it when you need to) - this is weird as TS is slathering type safety onto ducks
   * @throws Error if validation fails
   */
  insertRaw(appLogsToInsert) {
    DEBUG7(`[WriteableThread.insertRaw] ENTER - ${appLogsToInsert.length} applogs for thread "${this.name}"`);
    DEBUG7(`[WriteableThread.insertRaw] About to deduplicate`);
    const deduplicated = removeDuplicateAppLogs(appLogsToInsert, "safety");
    if (deduplicated.length !== appLogsToInsert.length) {
      throw ERROR7(`[insertRaw] duplicate applogs passed: ${appLogsToInsert.length - deduplicated.length}`, {
        appLogsToInsert,
        deduplicated
      });
    }
    DEBUG7(`[WriteableThread.insertRaw] Deduplication done`);
    DEBUG7(`[WriteableThread.insertRaw] About to validate`);
    const bogus = appLogsToInsert.filter((log) => !isValidApplog(log));
    if (bogus.length) {
      throw ERROR7(`[insertRaw] bogus applogs passed: ${bogus.length}`, { bogus });
    }
    DEBUG7(`[WriteableThread.insertRaw] Validation done`);
    DEBUG7(`[WriteableThread.insertRaw] About to check for existing`);
    const existing = appLogsToInsert.filter((log) => this.hasApplog(log, false));
    if (existing.length) {
      throw ERROR7(`[insertRaw] already existing applogs passed: ${existing.length}`, { existing });
    }
    DEBUG7(`[WriteableThread.insertRaw] Existing check done`);
    if (!appLogsToInsert.length) {
      WARN7("[insertRaw] skipping empty insert empty logs array");
      return;
    }
    ;
    (!this.hasParents && !(this instanceof ThreadInMemory) ? LOG7 : DEBUG7)(
      "Inserting:",
      appLogsToInsert.length === 1 ? appLogsToInsert[0] : appLogsToInsert,
      { ds: this }
    );
    DEBUG7(`[WriteableThread.insertRaw] About to sort applogs`);
    sortApplogsByTs(appLogsToInsert);
    const sortNeeded = this._applogs.length && isTsBefore(appLogsToInsert[0], this._applogs[this._applogs.length - 1]);
    DEBUG7(`[WriteableThread.insertRaw] About to push to _applogs array`);
    const CHUNK_SIZE = 5e4;
    for (let i = 0; i < appLogsToInsert.length; i += CHUNK_SIZE) {
      this._applogs.push(...appLogsToInsert.slice(i, i + CHUNK_SIZE));
    }
    if (sortNeeded) {
      DEBUG7(`[WriteableThread.insertRaw] About to sort _applogs (sortNeeded=true)`);
      sortApplogsByTs(this._applogs);
    }
    DEBUG7(`[WriteableThread.insertRaw] About to notify subscribers`);
    this.notifySubscribers({ added: appLogsToInsert, removed: null });
    DEBUG7(`[WriteableThread.insertRaw] Subscribers notified`);
    DEBUG7(`[WriteableThread.insertRaw] About to call persist (void - not awaited)`);
    void this.persist(appLogsToInsert);
    DEBUG7(`[WriteableThread.insertRaw] EXIT - returning ${appLogsToInsert.length} applogs`);
    return appLogsToInsert;
  }
  get readOnly() {
    return false;
  }
};
var ThreadInMemory = class _ThreadInMemory extends WriteableThread {
  constructor(name, applogs, filters, _readOnly) {
    super(name, applogs, filters);
    this._readOnly = _readOnly;
  }
  _readOnly;
  static empty(name) {
    return _ThreadInMemory.fromArray([], name ?? "empty in-memory", false);
  }
  static fromArray(applogs, name, readOnly = false) {
    return new _ThreadInMemory(name ?? "in-memory", applogs, [], readOnly);
  }
  static fromReadOnlyArray(applogs, name) {
    return new _ThreadInMemory(name ?? "in-memory", applogs, [], true);
  }
  get readOnly() {
    return this._readOnly;
  }
  async persist(logs) {
    VERBOSE7(`[InMem.persist] no persist for`, logs);
    if (this.readOnly) {
      throw ERROR7(`[persist] called for readOnly thread`);
    }
  }
};

// src/thread/utils.ts
import { Logger as Logger8 } from "besonders-logger";
import { debounce, uniq, uniqWith } from "lodash-es";
var { WARN: WARN8, LOG: LOG8, DEBUG: DEBUG8, VERBOSE: VERBOSE8, ERROR: ERROR8 } = Logger8.setup(Logger8.INFO);
function entityCount(thread) {
  return allEntityIDs(thread).size;
}
function allEntityIDs(thread) {
  return accumulateLogsToSet(thread, (log) => log.en);
}
function accumulateLogsToSet(threadOrLogs, callback) {
  const logs = threadOrLogs instanceof Thread ? threadOrLogs.applogs : threadOrLogs;
  const set = /* @__PURE__ */ new Set();
  for (const log of logs) {
    set.add(callback(log, set));
  }
  return set;
}
function debounceWrites(thread, wait = 700, removeDuplicatesWith = compareApplogsByEnAt) {
  if (thread.readOnly) throw ERROR8(`[debounceWrites] but readOnly thread`, thread.name);
  let insertQueue = [];
  const debouncedCommit = debounce(() => {
    DEBUG8(`Debounce tail`, { thread: thread.nameAndSizeUntracked, queued: applogsSummary(insertQueue) });
    const toInsert = ensureTsPvAndFinalizeApplogs(
      // ? uniq, sure - but which one is used? (update: seems the first one, so reverse)
      uniqWith(insertQueue.reverse(), removeDuplicatesWith),
      thread
    );
    thread.insertRaw(toInsert);
    insertQueue.splice(0, insertQueue.length);
  }, wait);
  const handleInsert = (applogs) => {
    DEBUG8(`Debounce input:`, applogsSummary(applogs), { thread: thread.nameAndSizeUntracked, queued: insertQueue.length });
    insertQueue.push(...applogs);
    debouncedCommit();
    return null;
  };
  const mappedThread = MappedThread.mapWrites(
    thread,
    `Debounce(${wait})`,
    handleInsert
  );
  return mappedThread;
}
function holdTillFirstWrite(thread, applogsToHold, opts) {
  DEBUG8(`[holdTillFirstWrite] holding logs:`, { applogsToHold });
  if (thread.readOnly) throw ERROR8(`[holdTillFirstWrite] but readOnly thread`, thread.nameAndSizeUntracked);
  const heldLogs = ensureTsPvAndFinalizeApplogs([...applogsToHold], thread);
  const heldThread = ThreadInMemory.fromArray(heldLogs);
  let hasInserted = false;
  const handleInsert = (realApplogs) => {
    if (hasInserted) return realApplogs;
    hasInserted = true;
    let toInsert = [...heldLogs];
    if (opts.deduplicateHoldItemsWith) {
      toInsert = toInsert.filter(
        (heldLog) => (
          // some duplicate? so don't insert
          !realApplogs.some((realLog) => opts.deduplicateHoldItemsWith(heldLog, realLog))
        )
      );
    }
    if (opts.onFirstWrite) {
      const callbackResult = opts.onFirstWrite(toInsert);
      if (callbackResult !== void 0) {
        toInsert = callbackResult;
      }
    }
    DEBUG8(`[holdTillFirstWrite] adding hold logs:`, { applogsToHold, heldLogs, toInsert, realApplogs });
    return [...toInsert, ...realApplogs];
  };
  const joinedThread = joinThreads([thread, heldThread]);
  return new MappedThread(
    `HoldTillFirstWrite[${applogsToHold.length}]<${thread.nameAndSizeUntracked}>`,
    joinedThread,
    thread.filters,
    null,
    // no derivation — shares joinedThread's array
    (applogs) => {
      const logsToInsert = handleInsert(applogs);
      thread.insert(logsToInsert);
      return null;
    }
  );
}
function getAgents(thread) {
  return uniq(thread.map((l) => l.ag));
}
var excludeApplogsContainedIn = (applogs, exclude) => {
  const excludeCids = exclude instanceof Set ? new Set([...exclude].map((c) => c.toString())) : exclude instanceof Thread ? new Set(exclude.applogsCids) : new Set(exclude.map((a) => a.cid));
  return applogs.filter((applog) => {
    if (!applog.cid) {
      ERROR8(`applog with missing CID`, applog);
      throw new Error(`applog with missing CID`);
    }
    return !excludeCids.has(applog.cid);
  });
};

export {
  applogSummary,
  applogsSummary,
  isoDateStrCompare,
  objEqualByKeys,
  valueEq,
  valueKey,
  compareApplogsByTs,
  compareApplogsByEnAt,
  isLaterByTsAndPv,
  sortApplogsByTs,
  isTsBefore,
  uniqueEnFromAppLogs,
  areApplogsEqual,
  removeDuplicateAppLogs,
  mintNewEntity,
  deriveDeterministicEntityID,
  isVariable,
  variableNameWithoutQuestionmark,
  isStaticPattern,
  resolveOrRemoveVariables,
  matchPartStatic,
  matchPart,
  matchPattern,
  actualize,
  arrStats,
  tsNearlySame,
  cyrb53hash,
  arraysContainSameElements,
  dateNowIso,
  SubscribableImpl,
  isArrayInitEvent,
  SubscribableArrayImpl,
  createDebugName,
  createDebugNameObj,
  prettifyThreadName,
  isInitEvent,
  Thread,
  getLogsFromThread,
  StaticThread,
  memoizedFn,
  refCountedMemoizedFn,
  MappedThread,
  rollingFilter2 as rollingFilter,
  rollingMapper,
  rollingAcc,
  getUntrackedPattern,
  makeFilter,
  getUntrackedFilterResults,
  hasFilter,
  assertRaw,
  assertOnlyCurrent,
  asReadOnly,
  simpleApplogMapper,
  includes,
  includedIn,
  anyOf,
  applogsByEntity,
  applogsByAttrValue,
  entityLinkIndex,
  WriteableThread,
  ThreadInMemory,
  entityCount,
  allEntityIDs,
  debounceWrites,
  holdTillFirstWrite,
  getAgents,
  excludeApplogsContainedIn,
  ensureTsPvAndFinalizeApplogs,
  ensureTsPvAndFinalizeApplog,
  finalizeApplogForInsert,
  hasAg,
  hasTs,
  hasPv,
  withTs,
  withAg,
  withPvFrom,
  joinThreads
};
//# sourceMappingURL=chunk-236RMLMX.js.map